import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useSearchParams, useLocation } from 'react-router';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/Layout';

// Auth pages (lazy)
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));

// App pages (lazy)
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const CredentialsPage = lazy(() => import('@/pages/CredentialsPage'));
const CredentialDetailPage = lazy(() => import('@/pages/CredentialDetailPage'));
const CredentialFormPage = lazy(() => import('@/pages/CredentialFormPage'));
const FoldersPage = lazy(() => import('@/pages/FoldersPage'));
const ShareLinksPage = lazy(() => import('@/pages/ShareLinksPage'));
const ShareViewPage = lazy(() => import('@/pages/ShareViewPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const ExportPage = lazy(() => import('@/pages/ExportPage'));

// Admin pages (lazy)
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'));
const InvitesPage = lazy(() => import('@/pages/admin/InvitesPage'));
const AuditLogsPage = lazy(() => import('@/pages/admin/AuditLogsPage'));
const CpanelSyncPage = lazy(() => import('@/pages/admin/CpanelSyncPage'));
const CpanelLogsPage = lazy(() => import('@/pages/admin/CpanelLogsPage'));

// Knowledge Base pages (lazy)
const KBHomePage = lazy(() => import('@/pages/kb/KBHomePage'));
const KBFolderPage = lazy(() => import('@/pages/kb/KBFolderPage'));
const KBArticlePage = lazy(() => import('@/pages/kb/KBArticlePage'));
const KBArticleFormPage = lazy(() => import('@/pages/kb/KBArticleFormPage'));
const KBPublicArticlePage = lazy(() => import('@/pages/kb/KBPublicArticlePage'));
const KBTemplatesPage = lazy(() => import('@/pages/kb/KBTemplatesPage'));

// Company pages (lazy)
const CompaniesListPage = lazy(() => import('@/pages/companies/CompaniesListPage'));
const CompanyFormPage = lazy(() => import('@/pages/companies/CompanyFormPage'));
const CompanyLayout = lazy(() => import('@/pages/companies/CompanyLayout'));
const CompanyOverviewPage = lazy(() => import('@/pages/companies/CompanyOverviewPage'));
const CompanyCredentialsPage = lazy(() => import('@/pages/companies/CompanyCredentialsPage'));
const CompanyCredentialDetailPage = lazy(() => import('@/pages/companies/CompanyCredentialDetailPage'));
const CompanyCredentialFormPage = lazy(() => import('@/pages/companies/CompanyCredentialFormPage'));
const CompanyFoldersPage = lazy(() => import('@/pages/companies/CompanyFoldersPage'));
const CompanyContactsPage = lazy(() => import('@/pages/companies/CompanyContactsPage'));
const CompanyMembersPage = lazy(() => import('@/pages/companies/CompanyMembersPage'));
const CompanyActivityPage = lazy(() => import('@/pages/companies/CompanyActivityPage'));
const CompanyKBHomePage = lazy(() => import('@/pages/companies/CompanyKBHomePage'));
const CompanyKBArticlePage = lazy(() => import('@/pages/companies/CompanyKBArticlePage'));
const CompanyKBArticleFormPage = lazy(() => import('@/pages/companies/CompanyKBArticleFormPage'));
const CustomFieldsPage = lazy(() => import('@/pages/companies/CustomFieldsPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  );
}

function PrivateRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = location.pathname !== '/' ? `?returnTo=${encodeURIComponent(location.pathname)}` : '';
    return <Navigate to={`/login${returnTo}`} replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function AdminRoute() {
  const { user } = useAuth();

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Check for returnTo parameter and redirect there instead of home
    const returnTo = searchParams.get('returnTo');
    const destination = returnTo && returnTo.startsWith('/') ? returnTo : '/';
    return <Navigate to={destination} replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public views - accessible without auth */}
        <Route path="/share/:token" element={<ShareViewPage />} />
        <Route path="/kb/share/:token" element={<KBPublicArticlePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Auth routes - redirect if logged in */}
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Protected routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/credentials" element={<CredentialsPage />} />
          <Route path="/credentials/new" element={<CredentialFormPage />} />
          <Route path="/credentials/:id" element={<CredentialDetailPage />} />
          <Route path="/credentials/:id/edit" element={<CredentialFormPage />} />
          <Route path="/folders" element={<FoldersPage />} />
          <Route path="/share-links" element={<ShareLinksPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/export" element={<ExportPage />} />

          {/* Knowledge Base routes */}
          <Route path="/kb" element={<KBHomePage />} />
          <Route path="/kb/templates" element={<KBTemplatesPage />} />
          <Route path="/kb/folders/:folderId" element={<KBFolderPage />} />
          <Route path="/kb/articles/new" element={<KBArticleFormPage />} />
          <Route path="/kb/articles/:id" element={<KBArticlePage />} />
          <Route path="/kb/articles/:id/edit" element={<KBArticleFormPage />} />

          {/* Company routes */}
          <Route path="/companies" element={<CompaniesListPage />} />
          <Route path="/companies/new" element={<CompanyFormPage />} />
          <Route path="/companies/:companyId" element={<CompanyLayout />}>
            <Route index element={<CompanyOverviewPage />} />
            <Route path="credentials" element={<CompanyCredentialsPage />} />
            <Route path="credentials/new" element={<CompanyCredentialFormPage />} />
            <Route path="credentials/:credentialId" element={<CompanyCredentialDetailPage />} />
            <Route path="credentials/:credentialId/edit" element={<CompanyCredentialFormPage />} />
            <Route path="folders" element={<CompanyFoldersPage />} />
            <Route path="contacts" element={<CompanyContactsPage />} />
            <Route path="members" element={<CompanyMembersPage />} />
            <Route path="activity" element={<CompanyActivityPage />} />
            <Route path="kb" element={<CompanyKBHomePage />} />
            <Route path="kb/articles/new" element={<CompanyKBArticleFormPage />} />
            <Route path="kb/articles/:articleId" element={<CompanyKBArticlePage />} />
            <Route path="kb/articles/:articleId/edit" element={<CompanyKBArticleFormPage />} />
            <Route path="custom-fields" element={<CustomFieldsPage />} />
            <Route path="settings" element={<CompanyFormPage />} />
          </Route>

          {/* Admin routes */}
          <Route element={<AdminRoute />}>
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/invites" element={<InvitesPage />} />
            <Route path="/admin/audit-logs" element={<AuditLogsPage />} />
            <Route path="/admin/cpanel-sync" element={<CpanelSyncPage />} />
            <Route path="/admin/cpanel-logs" element={<CpanelLogsPage />} />
          </Route>
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}
